import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, ExternalLink, Mail, Check,
  Gauge, SearchX, FilePenLine, CloudOff,
  Palette, Boxes, Sparkles, HardDrive,
  LayoutTemplate, Smartphone, ShieldCheck, PenSquare, Crop, FolderOpen,
  Users, Send, CreditCard, BarChart3, Database, Lock,
  Zap, Search, Bot, Quote, User,
} from 'lucide-react';
import '@/styles/casestudy.css';
import useReveal from '@/hooks/useReveal';
import { useSeo } from '@/lib/seo';
import { CountUp } from '@/components/CountUp';
import { CaseStudyNav } from '@/components/CaseStudyNav';
import RelatedJournal from '@/components/RelatedJournal';
import { ScrollProgress } from '@/components/ScrollProgress';
import { BackToTop } from '@/components/BackToTop';
import {
  CS_IMAGES, BOOST_LOGO, CS_HERO, CS_CLIENT, CS_CHALLENGE, CS_APPROACH,
  CS_SOLUTION, CS_RESULTS, CS_TECH, CS_TESTIMONIAL, CS_CTA,
} from '@/data/imprintCaseStudy';

const CANONICAL = 'https://boostmarketing.agency/case-studies/imprint-mis';
const SEO_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Reimagining the future of intelligent printing — Imprint MIS case study',
    description:
      "How Boost! rebuilt Imprint MIS's website and custom full-stack platform — faster, SEO/GEO-first and built to be cited by AI search.",
    image: CS_IMAGES.hero,
    author: { '@type': 'Organization', name: 'BOOST! Design & Marketing', url: 'https://boostmarketing.agency/' },
    publisher: { '@type': 'Organization', name: 'BOOST! Design & Marketing', logo: { '@type': 'ImageObject', url: BOOST_LOGO } },
    about: { '@type': 'Organization', name: 'Imprint Business Systems Limited', url: 'https://imprint-mis.co.uk' },
    keywords: 'website design, full-stack build, SEO, GEO, generative engine optimisation, print MIS software, packaging MIS, JDF, CIP4',
    mainEntityOfPage: { '@type': 'WebPage', '@id': CANONICAL },
    inLanguage: 'en-GB',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://boostmarketing.agency/' },
      { '@type': 'ListItem', position: 2, name: 'Work', item: 'https://boostmarketing.agency/work' },
      { '@type': 'ListItem', position: 3, name: 'Imprint MIS', item: CANONICAL },
    ],
  },
];

const ICONS = {
  Gauge, SearchX, FilePenLine, CloudOff, Palette, Boxes, Sparkles, HardDrive,
  LayoutTemplate, Smartphone, ShieldCheck, PenSquare, Crop, FolderOpen,
  Users, Send, CreditCard, BarChart3, Database, Lock, Zap, Search, Bot, Mail,
};

function Reveal({ children, className = '', delay = 0, as: Tag = 'div', ...rest }) {
  const ref = useReveal();
  return (
    <Tag ref={ref} className={`cs-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}

function Frame({ src, url, alt, className = '' }) {
  return (
    <div className={`cs-frame ${className}`}>
      <div className="cs-frame-bar">
        <i /><i /><i />
        <span className="cs-frame-url">{url}</span>
      </div>
      <img src={src} alt={alt} loading="lazy" />
    </div>
  );
}

function Hero() {
  return (
    <header className="cs-hero" data-testid="cs-hero">
      <div className="cs-container">
        <Reveal className="cs-hero-eyebrow" as="span">
          <span className="cs-dot" /> {CS_HERO.eyebrow}
        </Reveal>
        <Reveal>
          <h1 className="cs-hero-title" data-testid="cs-hero-title">
            Reimagining the future of <span className="cs-hl">intelligent printing</span>
          </h1>
        </Reveal>
        <Reveal delay={80}>
          <p className="cs-hero-sub">{CS_HERO.sub}</p>
        </Reveal>
        <Reveal delay={140} className="cs-hero-meta">
          <div className="cs-chips">
            {CS_HERO.chips.map((c) => (
              <span className="cs-chip" key={c}>{c}</span>
            ))}
          </div>
          <span className="cs-year">{CS_HERO.year}</span>
        </Reveal>
        <Reveal delay={180}>
          <a className="cs-hero-live" href={CS_HERO.liveUrl} target="_blank" rel="noopener noreferrer" data-testid="cs-hero-live-link">
            <span className="cs-client-tag">Client:</span> {CS_HERO.client}
            <ExternalLink size={16} />
          </a>
        </Reveal>
        <Reveal delay={120} className="cs-hero-figure">
          <Frame src={CS_IMAGES.hero} url="imprint-mis.co.uk" alt="Imprint MIS homepage — animated robot-mascot hero" />
        </Reveal>
      </div>
    </header>
  );
}

function ClientSection() {
  return (
    <section className="cs-section" data-testid="cs-client">
      <div className="cs-container">
        <div className="cs-client-grid">
          <Reveal>
            <span className="cs-kicker">{CS_CLIENT.kicker}</span>
            <h2 className="cs-h2">{CS_CLIENT.title}</h2>
          </Reveal>
          <Reveal delay={100}>
            {CS_CLIENT.copy.map((p) => (
              <p className="cs-lead" key={p.slice(0, 20)} style={{ marginTop: 0, marginBottom: 18 }}>{p}</p>
            ))}
            <div className="cs-facts">
              {CS_CLIENT.facts.map((f) => (
                <div className="cs-fact" key={f.value}>
                  <div className="cs-fact-value">{f.value}</div>
                  <div className="cs-fact-label">{f.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ChallengeSection() {
  return (
    <section className="cs-section cs-section-alt" data-testid="cs-challenge">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{CS_CHALLENGE.kicker}</span>
          <h2 className="cs-h2">{CS_CHALLENGE.title}</h2>
        </Reveal>
        <div className="cs-challenge-grid">
          {CS_CHALLENGE.items.map((item, i) => {
            const Icon = ICONS[item.icon];
            return (
              <Reveal className="cs-challenge-card" key={item.title} delay={i * 70}>
                <span className="cs-x-icon"><Icon size={22} /></span>
                <h3 className="cs-card-title">{item.title}</h3>
                <p className="cs-card-copy">{item.copy}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ApproachSection() {
  return (
    <section className="cs-section" data-testid="cs-approach">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{CS_APPROACH.kicker}</span>
          <h2 className="cs-h2">{CS_APPROACH.title}</h2>
        </Reveal>
        <div className="cs-approach-grid">
          {CS_APPROACH.items.map((item, i) => {
            const Icon = ICONS[item.icon];
            return (
              <Reveal className="cs-approach-card" key={item.title} delay={i * 70}>
                <span className="cs-approach-num">{String(i + 1).padStart(2, '0')}</span>
                <span className="cs-x-icon"><Icon size={22} /></span>
                <h3 className="cs-card-title">{item.title}</h3>
                <p className="cs-card-copy">{item.copy}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FeatureGrid({ features }) {
  return (
    <div className="cs-feature-grid">
      {features.map((f, i) => {
        const Icon = ICONS[f.icon];
        return (
          <Reveal className="cs-feature-card" key={f.title} delay={(i % 3) * 70}>
            <span className="cs-feature-icon"><Icon size={22} /></span>
            <h3 className="cs-card-title">{f.title}</h3>
            <p className="cs-card-copy">{f.copy}</p>
          </Reveal>
        );
      })}
    </div>
  );
}

function SolutionSection() {
  const s = CS_SOLUTION;
  return (
    <section className="cs-section cs-section-alt" data-testid="cs-solution">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{s.kicker}</span>
          <h2 className="cs-h2">{s.title}</h2>
        </Reveal>

        <Reveal className="cs-sol-label" as="div">{s.public.label}</Reveal>
        <FeatureGrid features={s.public.features} />

        <Reveal className="cs-sol-label" as="div">{s.admin.label}</Reveal>
        <Reveal><p className="cs-sol-sub">{s.admin.sub}</p></Reveal>
        <FeatureGrid features={s.admin.features} />

        <div className="cs-shots">
          {s.shots.map((shot, i) => (
            <Reveal className="cs-shot" as="figure" key={shot.url} delay={(i % 2) * 90} style={{ margin: 0, transitionDelay: `${(i % 2) * 90}ms` }}>
              <Frame src={shot.src} url={shot.url} alt={shot.caption} />
              <figcaption>{shot.caption}</figcaption>
            </Reveal>
          ))}
        </div>

        <Reveal className="cs-sol-label" as="div">{s.builtToLast.label}</Reveal>
        <div className="cs-btl">
          {s.builtToLast.items.map((item, i) => {
            const Icon = ICONS[item.icon];
            return (
              <Reveal className="cs-btl-card" key={item.title} delay={i * 70}>
                <span className="cs-x-icon"><Icon size={20} /></span>
                <div>
                  <h3 className="cs-card-title" style={{ fontSize: 17 }}>{item.title}</h3>
                  <p className="cs-card-copy">{item.copy}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ResultsSection() {
  const r = CS_RESULTS;
  return (
    <section className="cs-section" data-testid="cs-results">
      <div className="cs-container">
        <Reveal>
          <span className="cs-kicker">{r.kicker}</span>
          <h2 className="cs-h2">{r.title}</h2>
        </Reveal>

        <div className="cs-stats">
          {r.headline.map((stat, i) => (
            <Reveal className="cs-stat" key={stat.label} delay={i * 70}>
              <div className="cs-stat-value">
                <CountUp value={stat.value} />{stat.unit && <span className="cs-unit">{stat.unit}</span>}
              </div>
              <div className="cs-stat-label">{stat.label}</div>
              <div className="cs-stat-note">{stat.note}</div>
            </Reveal>
          ))}
        </div>

        <div className="cs-breakdown">
          {r.groups.map((g, i) => {
            const Icon = ICONS[g.icon];
            return (
              <Reveal className="cs-bd-card" key={g.title} delay={i * 80}>
                <div className="cs-bd-head">
                  <span className="cs-x-icon" style={{ marginBottom: 0, width: 40, height: 40 }}><Icon size={19} /></span>
                  <h4>{g.title}</h4>
                </div>
                <ul className="cs-bd-list">
                  {g.points.map((p) => (
                    <li key={p.slice(0, 20)}><Check size={15} /> <span>{p}</span></li>
                  ))}
                </ul>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="cs-crawlers-wrap">
          <div className="cs-crawlers-label">{r.crawlers.length} named AI crawlers explicitly welcomed</div>
          <div className="cs-crawlers">
            {r.crawlers.map((c) => (
              <span className="cs-crawler" key={c}>{c}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TechStrip() {
  return (
    <section className="cs-tech" data-testid="cs-tech">
      <div className="cs-container">
        <Reveal>
          <div className="cs-tech-label">Tech &amp; capabilities</div>
          <div className="cs-tech-strip">
            {CS_TECH.map((t) => (
              <span className="cs-tech-item" key={t}>{t}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Testimonial() {
  const t = CS_TESTIMONIAL;
  return (
    <section className="cs-section" id="testimonial" data-testid="cs-testimonial" style={{ paddingBottom: 'clamp(40px, 6vw, 70px)' }}>
      <div className="cs-container">
        <Reveal className="cs-quote">
          <Quote className="cs-quote-mark" size={64} strokeWidth={0} fill="currentColor" style={{ opacity: 0.35 }} />
          <p className="cs-quote-text">{t.quote}</p>
          <div className="cs-quote-author">
            {t.photo
              ? <img className="cs-quote-photo" src={t.photo} alt={t.name} />
              : <span className="cs-quote-photo-empty"><User size={24} /></span>}
            <div>
              <div className="cs-quote-name">{t.name}</div>
              <div className="cs-quote-role">{t.role}</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ClosingCta() {
  const c = CS_CTA;
  return (
    <section className="cs-cta-section" data-testid="cs-cta">
      <div className="cs-container">
        <Reveal>
          <h2 className="cs-cta-title">{c.title}</h2>
          <p className="cs-cta-sub">{c.sub}</p>
          <div className="cs-cta-btns">
            <a className="cs-btn cs-btn-primary" href={`mailto:${c.email}`} data-testid="cs-cta-email">
              Start a project <ArrowUpRight size={18} />
            </a>
            <a className="cs-btn cs-btn-ghost" href={c.siteUrl} target="_blank" rel="noopener noreferrer" data-testid="cs-cta-site">
              Visit boostmarketing.agency <ExternalLink size={16} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function CaseStudyFooter() {
  return (
    <footer className="cs-footer" data-testid="cs-footer">
      <div className="cs-container cs-footer-inner">
        <div className="cs-footer-brand">
          <img src={BOOST_LOGO} alt="Boost! Design & Marketing" />
          <div className="cs-footer-credit">
            Designed &amp; built by <strong>Boost! Design &amp; Marketing Ltd</strong>
            <br />© {new Date().getFullYear()} — Giving your brand lift-off.
          </div>
        </div>
        <a className="cs-footer-link" href="https://www.boostmarketing.agency" target="_blank" rel="noopener noreferrer" data-testid="cs-footer-link">
          www.boostmarketing.agency <ArrowUpRight size={15} />
        </a>
      </div>
    </footer>
  );
}

export default function CaseStudyImprint() {
  useSeo({
    title: 'Imprint MIS Case Study — Website & Custom Platform Build | Boost!',
    description:
      "How Boost! rebuilt Imprint MIS's website and custom full-stack platform — faster, SEO/GEO-first and built to be cited by AI. Web design, headless CMS, CRM, analytics and more.",
    canonical: CANONICAL,
    image: 'https://boostmarketing.agency/og-imprint.png',
    jsonLd: SEO_JSONLD,
  });
  return (
    <div className="cs-page" data-testid="case-study-imprint">
      <div className="cs-grain" />
      <div className="cs-glows" />
      <ScrollProgress />
      <Link to="/work" className="cs-back" data-testid="cs-back-to-work">
        <ArrowLeft size={16} /> Back to work
      </Link>
      <main>
        <Hero />
        <ClientSection />
        <ChallengeSection />
        <ApproachSection />
        <SolutionSection />
        <ResultsSection />
        <TechStrip />
        <Testimonial />
        <ClosingCta />
        <RelatedJournal caseStudy="imprint-mis" />
        <CaseStudyNav
          to="/case-studies/temple-of-sound"
          title="Two legends, one house"
          client="Temple of Sound · Bespoke retail platform"
        />
      </main>
      <CaseStudyFooter />
      <BackToTop />
    </div>
  );
}
